import { parse } from '@babel/parser';
import * as t from '@babel/types';

export interface ComponentBlock {
	tagName: string;
	className: string;
	classStartIndex: number;
	classEndIndex: number;
	defineStartIndex: number;
	defineEndIndex: number;
}

export interface ParsedComponents {
	blocks: ComponentBlock[];
	source: string;
}

type AstNode = t.Node | null | undefined;

// Walks a Babel AST depth-first, calling visit for each node.
function walkAst(node: AstNode, visit: (node: t.Node) => void): void {
	if (!node || typeof node !== 'object') return;
	visit(node);

	for (const value of Object.values(node)) {
		if (Array.isArray(value)) {
			for (const child of value) {
				if (child && typeof child === 'object' && 'type' in child) {
					walkAst(child as t.Node, visit);
				}
			}
		} else if (value && typeof value === 'object' && 'type' in value) {
			walkAst(value as t.Node, visit);
		}
	}
}

// Parse components.js using Babel AST to extract class declarations and customElements.define calls
// Returns component blocks with exact start/end positions for reliable code extraction
export function parseComponentAST(jsString: string): ParsedComponents {
	const blocks: ComponentBlock[] = [];
	const classMap = new Map<string, { start: number; end: number }>();
	const defineCalls: Array<{
		tagName: string;
		className: string;
		start: number;
		end: number;
	}> = [];

	try {
		const ast = parse(jsString, {
			sourceType: 'script',
			plugins: ['classProperties', 'decorators-legacy'],
			allowReturnOutsideFunction: true,
		});

		walkAst(ast, (node) => {
			if (t.isClassDeclaration(node)) {
				if (node.id && t.isIdentifier(node.id)) {
					classMap.set(node.id.name, {
						start: node.start ?? 0,
						end: node.end ?? jsString.length,
					});
				}
				return;
			}

			if (!t.isCallExpression(node)) return;

			const callee = node.callee;
			if (
				!t.isMemberExpression(callee) ||
				!t.isIdentifier(callee.object) ||
				callee.object.name !== 'customElements' ||
				!t.isIdentifier(callee.property) ||
				callee.property.name !== 'define'
			) {
				return;
			}

			const args = node.arguments;
			if (args.length < 2) return;

			const tagNameArg = args[0];
			const classNameArg = args[1];
			if (!t.isStringLiteral(tagNameArg) || !t.isIdentifier(classNameArg)) return;

			defineCalls.push({
				tagName: tagNameArg.value,
				className: classNameArg.name,
				start: node.start ?? 0,
				end: node.end ?? jsString.length,
			});
		});

		for (const defineCall of defineCalls) {
			const classInfo = classMap.get(defineCall.className);
			if (classInfo) {
				blocks.push({
					tagName: defineCall.tagName,
					className: defineCall.className,
					classStartIndex: classInfo.start,
					classEndIndex: classInfo.end,
					defineStartIndex: defineCall.start,
					defineEndIndex: defineCall.end,
				});
			}
		}

		blocks.sort((a, b) => a.classStartIndex - b.classStartIndex);
	} catch (error) {
		console.error('Failed to parse components.js with Babel AST:', error);
	}

	if (blocks.length === 0) {
		return parseComponentRegex(jsString);
	}

	return {
		blocks,
		source: jsString,
	};
}

// Regex-based fallback parser for malformed JavaScript
// This handles cases where template literals contain invalid syntax
function parseComponentRegex(jsString: string): ParsedComponents {
	const blocks: ComponentBlock[] = [];
	const classMap = new Map<string, { start: number; end: number }>();
	const defineCalls: Array<{
		tagName: string;
		className: string;
		start: number;
		end: number;
	}> = [];

	// Find all class declarations: class DSButton extends HTMLElement
	const classRegex = /class\s+([A-Z][A-Za-z0-9]+)\s+extends\s+HTMLElement/g;
	const classMatches: Array<{ className: string; start: number }> = [];
	let classMatch;
	while ((classMatch = classRegex.exec(jsString)) !== null) {
		classMatches.push({
			className: classMatch[1],
			start: classMatch.index,
		});
	}

	// For each class, find its end by looking for the next class or customElements.define
	for (let i = 0; i < classMatches.length; i++) {
		const { className, start: classStart } = classMatches[i];
		let classEnd = jsString.length; // Default to end of string

		// Find the next class declaration
		const nextClassStart =
			i < classMatches.length - 1 ? classMatches[i + 1].start : jsString.length;

		// Find the next customElements.define call after this class
		const afterClass = jsString.substring(classStart);
		const defineRegex = /customElements\.define\s*\(/g;
		let defineMatch;
		const definePositions: number[] = [];
		while ((defineMatch = defineRegex.exec(afterClass)) !== null) {
			definePositions.push(classStart + defineMatch.index);
		}

		// Find the define call that comes after this class but before the next class
		const relevantDefines = definePositions.filter(
			(pos) => pos >= classStart && pos < nextClassStart
		);

		if (relevantDefines.length > 0) {
			// The class should end before its corresponding define call
			// Try to find the closing brace before the define call
			const defineStart = relevantDefines[0];
			const beforeDefine = jsString.substring(classStart, defineStart);

			// Try to find matching braces, but be lenient with malformed code
			let braceCount = 0;
			let foundOpening = false;
			for (let j = 0; j < beforeDefine.length; j++) {
				const char = beforeDefine[j];
				if (char === '{') {
					braceCount++;
					foundOpening = true;
				} else if (char === '}' && foundOpening) {
					braceCount--;
					if (braceCount === 0) {
						classEnd = classStart + j + 1;
						break;
					}
				}
			}

			// If brace matching failed, use a heuristic: find the last } before the define
			if (braceCount > 0) {
				const lastBrace = beforeDefine.lastIndexOf('}');
				if (lastBrace > 0) {
					classEnd = classStart + lastBrace + 1;
				} else {
					// Fallback: end just before the define call
					classEnd = defineStart;
				}
			}
		} else if (nextClassStart < jsString.length) {
			// No define found, use next class as boundary
			classEnd = nextClassStart;
		}

		classMap.set(className, { start: classStart, end: classEnd });
	}

	// Find all customElements.define calls: customElements.define('ds-button', DSButton)
	const defineRegex =
		/customElements\.define\s*\(\s*['"]([^'"]+)['"]\s*,\s*([A-Z][A-Za-z0-9]+)\s*\)/g;
	let defineMatch;
	while ((defineMatch = defineRegex.exec(jsString)) !== null) {
		const tagName = defineMatch[1];
		const className = defineMatch[2];
		const start = defineMatch.index;
		const end = start + defineMatch[0].length;

		defineCalls.push({
			tagName,
			className,
			start,
			end,
		});
	}

	// Link customElements.define calls to their corresponding class declarations
	for (const defineCall of defineCalls) {
		const classInfo = classMap.get(defineCall.className);
		if (classInfo) {
			blocks.push({
				tagName: defineCall.tagName,
				className: defineCall.className,
				classStartIndex: classInfo.start,
				classEndIndex: classInfo.end,
				defineStartIndex: defineCall.start,
				defineEndIndex: defineCall.end,
			});
		}
	}

	// Sort blocks by class start index to maintain order
	blocks.sort((a, b) => a.classStartIndex - b.classStartIndex);

	return {
		blocks,
		source: jsString,
	};
}

// Extract class code from source using AST-determined boundaries
export function extractClassCode(block: ComponentBlock, source: string): string {
	return source.substring(block.classStartIndex, block.classEndIndex);
}

// Extract full component block (class + define) from source
export function extractComponentBlock(block: ComponentBlock, source: string): string {
	// Find the start of the class (might have comments before it)
	const classStart = block.classStartIndex;
	// Find the end of the define call
	const defineEnd = block.defineEndIndex;
	return source.substring(classStart, defineEnd);
}
